import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, Loader2, Send, Trash2, CheckCircle, MessageSquare,
  ThumbsUp, ThumbsDown, Link2, Megaphone, Eye, Edit2
} from "lucide-react";

export function CommunityViewDialog({
  open, onOpenChange, bulletinPosts, pendingComments,
  expandedPosts, postComments, loadingComments, newComment, setNewComment,
  submittingComment,
  onToggleExpand, onEditPost, onDeletePost,
  onApproveComment, onRejectComment, onDeleteComment, onAddComment,
  onCreatePost
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Eye className="w-5 h-5 mr-2 text-purple-600" /> Community View
          </DialogTitle>
          <DialogDescription>View and moderate community discussions</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="bulletin" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="bulletin">Bulletin & Comments</TabsTrigger>
            <TabsTrigger value="moderation" className="relative">
              Pending Moderation
              {pendingComments.length > 0 && (
                <span className="ml-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">{pendingComments.length}</span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bulletin" className="flex-1 overflow-y-auto mt-4 space-y-4">
            {bulletinPosts.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <Megaphone className="w-12 h-12 mx-auto mb-2 text-neutral-300" />
                <p>No bulletin posts yet</p>
                <Button className="mt-4" onClick={onCreatePost}><Plus className="w-4 h-4 mr-2" /> Create First Post</Button>
              </div>
            ) : (
              bulletinPosts.map((post) => (
                <Card key={post.post_id} className="border-purple-100">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CardTitle className="text-base">{post.title}</CardTitle>
                        {post.category && <Badge variant="outline" className="text-xs">{post.category}</Badge>}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => onToggleExpand(post.post_id)} className="text-purple-600">
                          <MessageSquare className="w-4 h-4 mr-1" /> {post.comment_count || 0}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => onEditPost(post)}><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onDeletePost(post.post_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-neutral-600 mb-2">{post.content}</p>
                    {post.link_url && (
                      <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="text-sm text-purple-600 hover:underline flex items-center">
                        <Link2 className="w-3 h-3 mr-1" /> {post.link_text || "View Link"}
                      </a>
                    )}
                    <p className="text-xs text-neutral-400 mt-2">
                      Posted by {post.author_name} - {new Date(post.created_at).toLocaleDateString()}
                    </p>

                    {expandedPosts[post.post_id] && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <h4 className="text-sm font-medium flex items-center"><MessageSquare className="w-4 h-4 mr-1" /> Comments</h4>
                        {loadingComments[post.post_id] ? (
                          <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>
                        ) : postComments[post.post_id]?.length === 0 ? (
                          <p className="text-sm text-neutral-500 text-center py-2">No comments yet</p>
                        ) : (
                          postComments[post.post_id]?.map((comment) => (
                            <div key={comment.comment_id} className={`p-3 rounded-lg text-sm ${comment.status === "pending" ? "bg-amber-50 border border-amber-200" : "bg-neutral-50"}`}>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="font-medium">{comment.author_name}</span>
                                    <Badge variant="outline" className="text-xs">
                                      {comment.author_role === "consultant" ? "Consultant" : comment.author_role === "client" ? "Client" : "Community"}
                                    </Badge>
                                    {comment.status === "pending" && <Badge className="bg-amber-100 text-amber-700 text-xs">Pending</Badge>}
                                    {comment.edited && <span className="text-xs text-neutral-400">(edited)</span>}
                                  </div>
                                  <p className="text-neutral-700">{comment.content}</p>
                                  <p className="text-xs text-neutral-400 mt-1">{new Date(comment.created_at).toLocaleString()}</p>
                                </div>
                                <div className="flex items-center space-x-1 ml-2">
                                  {comment.status === "pending" && (
                                    <>
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={() => onApproveComment(post.post_id, comment.comment_id)}>
                                        <ThumbsUp className="w-3 h-3" />
                                      </Button>
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => onRejectComment(post.post_id, comment.comment_id)}>
                                        <ThumbsDown className="w-3 h-3" />
                                      </Button>
                                    </>
                                  )}
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => onDeleteComment(post.post_id, comment.comment_id)}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                        <div className="flex items-center space-x-2 pt-2">
                          <Input
                            value={newComment[post.post_id] || ""}
                            onChange={(e) => setNewComment(prev => ({ ...prev, [post.post_id]: e.target.value }))}
                            placeholder="Write a comment..."
                            className="flex-1"
                            onKeyPress={(e) => e.key === "Enter" && onAddComment(post.post_id)}
                          />
                          <Button size="sm" onClick={() => onAddComment(post.post_id)} disabled={submittingComment || !newComment[post.post_id]?.trim()}>
                            {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="moderation" className="flex-1 overflow-y-auto mt-4">
            {pendingComments.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-300" />
                <p>No pending comments to moderate</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingComments.map((comment) => (
                  <Card key={comment.comment_id} className="border-amber-200 bg-amber-50">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="font-medium">{comment.author_name}</span>
                            <Badge variant="outline" className="text-xs">
                              {comment.author_role === "client" ? "Client" : "Community Member"}
                            </Badge>
                            <span className="text-xs text-neutral-500">on "{comment.post_title}"</span>
                          </div>
                          <p className="text-sm text-neutral-700 bg-white p-3 rounded-lg border">{comment.content}</p>
                          <p className="text-xs text-neutral-400 mt-2">Submitted {new Date(comment.created_at).toLocaleString()}</p>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onApproveComment(comment.post_id, comment.comment_id)}>
                            <ThumbsUp className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => onRejectComment(comment.post_id, comment.comment_id)}>
                            <ThumbsDown className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
